/**
 * /verify/monitoring-status/:id and /verify/monitoring-status/snapshot/:id — V113-7-4, the PUBLIC page a recipient
 * (insurer / customer) opens. No sign-in. It reads the same signed-fields-only JSON a machine gets (`?format=json`):
 * validity, history, per-system rows BY POSITION (no names: pack state, attested at, open issues, New), systems not
 * in the pack, out-of-scope decisions (reason + date, no item or person names), last verified. "Cite this snapshot"
 * copies the immutable snapshot url (says so on screen if the clipboard refuses). Signature details sit behind
 * Verify. A rotated link answers 410 → the replaced page. Modelled on /verify/monitoring-plan.
 */
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, BoardroomMode, Brand, Button, Card, Spinner } from '@aigovops/design-system';
import { ApiError } from '../lib/api';
import {
  getPublicMonitoringSnapshot, getPublicMonitoringStatus, type PublicLinkReplaced, type PublicMonitoringStatus, type PublicStatusSystem,
} from '../lib/monitoring-api';
import { absoluteUrl, attestedLine, carriedLine, carriedText, copyText, issuesText, newMarkerText, packMeta, shortDay } from '../lib/monitoring-view';
import { fmtDate, plural } from '../lib/format';
import { PackChip, StatusChip, VerifyDisclosure } from '../components/monitoring/parts';
import '../components/programs/strategy-compare.css';
import '../components/programs/plan-view.css';
import './today.css';
import './program-screens.css';
import './monitoring.css';

type Result = { kind: 'ok'; body: PublicMonitoringStatus } | { kind: 'replaced'; body: PublicLinkReplaced } | { kind: 'not_found' };

async function load(id: string, snapshot: boolean): Promise<Result> {
  try {
    return { kind: 'ok', body: await (snapshot ? getPublicMonitoringSnapshot(id) : getPublicMonitoringStatus(id)) };
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return { kind: 'not_found' };
    if (e instanceof ApiError && e.status === 410) return { kind: 'replaced', body: (e.body ?? { status: 'link_replaced', replaced_at: '', detail: '' }) as PublicLinkReplaced };
    throw e;
  }
}

function SystemsTable({ systems }: { systems: PublicStatusSystem[] }) {
  const inPack = systems.map((s, i) => ({ s, n: i + 1 })).filter((x) => x.s.in_pack);
  return (
    <table className="mon-table" role="table" aria-labelledby="vms-cov-h">
      <thead role="rowgroup">
        <tr role="row">
          <th role="columnheader" scope="col">System</th>
          <th role="columnheader" scope="col">Controls passing</th>
          <th role="columnheader" scope="col">Have a method</th>
          <th role="columnheader" scope="col">Attested at</th>
          <th role="columnheader" scope="col">Open issues</th>
          <th role="columnheader" scope="col">Pack status</th>
        </tr>
      </thead>
      <tbody role="rowgroup">
        {inPack.map(({ s, n }) => {
          const fresh = newMarkerText({ new_since_pack: s.new_since_pack, passing: s.covered, of: s.applicable });
          return (
            <tr role="row" key={n}>
              <td role="cell" className="mon-td-name" data-label="System"><span className="mon-name">System {n}</span></td>
              <td role="cell" data-label="Controls passing">{s.covered} of {s.applicable}</td>
              <td role="cell" data-label="Have a method">{s.monitored === null ? '—' : `${s.monitored} of ${s.applicable}`}</td>
              <td role="cell" data-label="Attested at">{attestedLine(s.attested_pass, s.attested_of).replace(/^attested at /, '') || '—'}</td>
              <td role="cell" data-label="Open issues">{issuesText(s.open_issues)}</td>
              <td role="cell" data-label="Pack status">
                <span className="mon-cell-stack">
                  <PackChip state={s.pack_state} />
                  {fresh ? <StatusChip label={fresh} tone="blue" glyph="circle" /> : null}
                  {s.new_since ? <span className="mon-sub">New since {fmtDate(s.new_since)}</span> : null}
                  {carriedLine(s.carried) ? <span className="mon-sub">{carriedLine(s.carried)}</span> : null}
                  {s.pack_state === 'current' ? <span className="mon-sub">Unchanged since attested</span> : null}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Status({ b, snapshot }: { b: PublicMonitoringStatus; snapshot: boolean }) {
  const [cite, setCite] = useState<'idle' | 'copied' | 'failed'>('idle');
  const current = b.history.find((h) => h.version === b.pack.version) ?? b.history[0];
  const since = current?.transitions[current.transitions.length - 1]?.at ?? b.pack.attested_at;
  const carried = carriedText(b.validity.carried);
  const verified = b.signature_valid && b.content_matches_digest;
  const notIn = b.coverage.systems.filter((s) => !s.in_pack).length;
  return (
    <>
      <Card>
        <div className="mon-col">
          <p className="mon-pack-line"><PackChip state={b.validity.status} large /><span className="mon-sub">since {fmtDate(since)}</span>{carried ? <strong>{carried}</strong> : null}</p>
          {b.validity.rules_pending ? <p className="mon-hint">Validity rules are pending review; the status may change once approved.</p> : null}
          <dl className="mon-dl">
            <dt>Pack attested</dt><dd>v{b.pack.version} · {fmtDate(b.pack.attested_at)}{b.pack.signed_with_exceptions ? ' · signed with exceptions' : ''}</dd>
            <dt>Last verified</dt><dd>{fmtDate(b.last_verified_at)}</dd>
            <dt>History</dt>
            <dd><ul className="mon-history" aria-label="Pack history, newest first">
              {b.history.map((h) => (
                <li key={h.version}>Pack v{h.version} · {h.replaced_at ? `${shortDay(h.attested_at)} – ${shortDay(h.replaced_at)}` : `attested ${shortDay(h.attested_at)}`}
                  {h.transitions.length ? `: ${h.transitions.map((t) => `${packMeta(t.status).label} ${shortDay(t.at)}`).join(' → ')}` : ''}
                  {carriedText(h.carried_at_signing) ? ` · signed carrying ${carriedText(h.carried_at_signing)}` : ''}</li>
              ))}
            </ul></dd>
            {b.validity.next_at_risk_at ? <><dt>Next at risk</dt><dd>{fmtDate(b.validity.next_at_risk_at)}</dd></> : null}
          </dl>
          {b.validity.reasons.length ? (
            <ul className="mon-reasons" aria-label="Why">
              {b.validity.reasons.map((r) => (
                <li key={r.code}>
                  <span><strong>{r.label}</strong>{r.since ? <span className="mon-sub"> · since {fmtDate(r.since)}</span> : null}</span>
                  <span className="mon-sub">{r.effect === 'invalidated' ? 'Invalidates the pack' : 'Makes the pack stale'} · {r.clears === 'resign' ? 'clears with a new pack' : 'clears when fixed'}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </Card>

      <Card density="compact" flush headingId="vms-cov-h" title="Coverage per system" right={<p className="mon-card-note">{plural(b.coverage.systems.length - notIn, 'system')} in pack</p>}>
        <SystemsTable systems={b.coverage.systems} />
      </Card>

      {b.coverage.systems_not_in_pack ? (
        <Card density="compact" headingId="vms-notin-h" title="Systems not in pack" right={<p className="mon-card-note">{b.coverage.systems_not_in_pack}</p>}>
          <p className="mon-hint">{b.coverage.systems_not_covered_label}</p>
        </Card>
      ) : null}

      {b.out_of_scope.length ? (
        <Card density="compact" headingId="vms-oos-h" title="Configuration items out of scope" right={<p className="mon-card-note">{b.out_of_scope.length}</p>}>
          <ul className="mon-rows">
            {b.out_of_scope.map((o, i) => (
              <li key={i} className="mon-row"><span className="mon-row-main">
                <span>Out of scope{o.reason ? ` · ${o.reason}` : ''}{o.decided_at ? ` · ${fmtDate(o.decided_at)}` : ''}</span>
                {o.failing ? <span className="mon-line"><StatusChip label="Its check is failing" tone="outline" glyph="x" /></span> : null}
              </span></li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card density="compact" headingId="vms-sig-h" title="Signature"
        right={<StatusChip label={verified ? 'Signature valid' : 'Does not check out'} tone={verified ? 'outline' : 'red'} glyph={verified ? 'check' : 'x'} />}>
        {!verified ? <Alert variant="warning">This status may have changed after it was signed. Ask the sender for a new link.</Alert> : null}
        <p className="mon-hint">{snapshot ? `Snapshot signed ${fmtDate(b.snapshot_signed_at)}` : `Live status · snapshot signed ${fmtDate(b.snapshot_signed_at)}`}</p>
        <div className="mon-actions mon-actions--stack">
          <Button onClick={async () => { setCite((await copyText(absoluteUrl(b.snapshot_url))) ? 'copied' : 'failed'); }}>{cite === 'copied' ? 'Snapshot link copied' : 'Cite this snapshot'}</Button>
          <a className="gp-linkbtn" href={`${b.snapshot_url}?format=json`} download>Download signed JSON</a>
        </div>
        <p role="status" aria-live="polite" className={cite === 'failed' ? 'mon-err' : 'gp-sr-only'}>
          {cite === 'failed' ? <>Copy didn’t work here. The snapshot link is <span className="mon-mono">{absoluteUrl(b.snapshot_url)}</span></> : cite === 'copied' ? 'Snapshot link copied' : ''}
        </p>
        <VerifyDisclosure label="Verify signature">
          <dl className="mon-dl">
            <dt>Snapshot digest (SHA-256)</dt><dd className="mon-mono">{b.content_sha256}</dd>
            <dt>Pack digest (SHA-256)</dt><dd className="mon-mono">{b.pack.content_sha256}</dd>
            <dt>Signature valid</dt><dd>{b.signature_valid ? 'Yes' : 'No'}</dd>
            <dt>Content matches the digest</dt><dd>{b.content_matches_digest ? 'Yes' : 'No'}</dd>
            {b.signing_key_retired ? <><dt>Signing key</dt><dd>Retired since; this snapshot still verifies</dd></> : null}
            {b.last_anomaly_at ? <><dt>Last anomaly</dt><dd>{fmtDate(b.last_anomaly_at)}</dd></> : null}
            {b.last_config_change_at ? <><dt>Last configuration change</dt><dd>{fmtDate(b.last_config_change_at)}</dd></> : null}
          </dl>
        </VerifyDisclosure>
      </Card>
    </>
  );
}

export function VerifyMonitoringStatus() {
  const { id = '' } = useParams();
  const snapshot = useLocation().pathname.includes('/monitoring-status/snapshot/');
  const q = useQuery({ queryKey: ['verify-monitoring-status', snapshot ? 'snapshot' : 'link', id], queryFn: () => load(id, snapshot), retry: false });
  return (
    <BoardroomMode>
      <div className="gp-verify-page">
        <header className="gp-verify-head">
          <div className="gp-verify-inner">
            <Link to="/" className="gp-verify-brand"><Brand variant="glyph" /><Brand variant="wordmark" /></Link>
          </div>
        </header>
        <main className="mon-verify">
          <div className="mon-col">
            <p className="mon-eyebrow">AI insurability pack</p>
            <h1>{snapshot ? 'Monitoring status snapshot' : 'Monitoring status'}</h1>
          </div>
          {q.isLoading ? <Spinner label="Checking the signature" /> : q.error ? (
            <Alert variant="error"><p>The status can’t be checked right now.</p><Button compact onClick={() => void q.refetch()}>Try again</Button></Alert>
          ) : q.data?.kind === 'replaced' ? (
            <Alert variant="warning" title="This link was replaced">
              <p>{q.data.body.replaced_at ? `Replaced ${fmtDate(q.data.body.replaced_at)}. ` : ''}Ask the company for its current monitoring status link.</p>
              <p>A snapshot you cited earlier still opens and verifies.</p>
            </Alert>
          ) : q.data?.kind === 'ok' ? <Status b={q.data.body} snapshot={snapshot} /> : (
            <Alert variant="warning"><p>No monitoring status has this link. Check that the whole link was copied, or ask the sender for a new one.</p></Alert>
          )}
          <footer className="mon-verify-foot"><span>Verified by GAC Pro · gacpro.io</span><span>Signed fields only</span></footer>
        </main>
      </div>
    </BoardroomMode>
  );
}
